import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { name: t('footer.links.indicators'), path: '/indicators' },
    { name: t('footer.links.contact'), path: '/#contact' },
  ];

  const legalLinks = [
    { name: t('footer.links.legalNotice'), path: '/legal' },
    { name: t('footer.links.privacy'), path: '/privacy' },
    { name: t('footer.links.terms'), path: '/terms' },
    { name: t('footer.links.cookies'), path: '/cookies' },
  ];

  return (
    <footer className="border-t border-white/[.06] bg-bg">
      <div className="mx-auto max-w-page px-6">
        <div className="grid grid-cols-1 items-start gap-10 border-b border-white/[.06] py-14 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-4">
            <Link
              to="/"
              className="mb-4 block text-xs font-medium uppercase tracking-[0.14em] text-body transition-colors hover:text-muted"
            >
              Alturion Labs
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-muted">
              {t('footer.description')}
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="mb-4 text-xs font-medium uppercase tracking-[0.14em] text-muted">
              {t('footer.navigation')}
            </h4>
            <ul className="space-y-2.5">
              {navLinks.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-sm text-muted transition-colors hover:text-body"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="mb-4 text-xs font-medium uppercase tracking-[0.14em] text-muted">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-2.5">
              {legalLinks.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-sm text-muted transition-colors hover:text-body"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="mb-4 text-xs font-medium uppercase tracking-[0.14em] text-muted">
              {t('footer.links.contact')}
            </h4>
            <a
              href="mailto:support@alturionlabs.com"
              className="inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-body"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              support@alturionlabs.com
            </a>
          </div>
        </div>

        <div className="border-b border-white/[.06] py-10">
          <div className="grid gap-8 md:grid-cols-12 md:items-start">
            <div className="flex flex-col items-start gap-6 md:col-span-2">
              <a
                href="https://ninjatraderdomesticvendor.sjv.io/Bn6731"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 transition-opacity hover:opacity-100"
              >
                <img
                  src="/images/Logos/NinjaTrader/NinjaTrader_Wordmark_color_RGB.png"
                  alt="NinjaTrader"
                  className="h-7"
                />
              </a>
              <a
                href="https://kinetick.com/NinjaTrader"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 transition-opacity hover:opacity-100"
              >
                <img
                  src="/images/Logos/Kinetick/Kinetick_Logo.png"
                  alt="Kinetick"
                  className="h-7"
                />
              </a>
            </div>

            <div className="space-y-3 text-xs leading-relaxed text-muted/80 md:col-span-10">
              <p>
                <strong className="font-medium text-muted">Risk Disclosure:</strong> Futures and
                forex trading contains substantial risk and is not for every investor. An investor
                could potentially lose all or more than the initial investment. Risk capital is
                money that can be lost without jeopardizing ones&apos; financial security or
                lifestyle. Only risk capital should be used for trading and only those with
                sufficient risk capital should consider trading. Past performance is not necessarily
                indicative of future results.
              </p>
              <p>
                <strong className="font-medium text-muted">Hypothetical Performance Disclosure:</strong>{' '}
                Hypothetical performance results have many inherent limitations. No representation
                is being made that any account will or is likely to achieve profits or losses
                similar to those shown. There are frequently sharp differences between hypothetical
                performance results and the actual results subsequently achieved by any particular
                trading program.
              </p>
              <p>
                <strong className="font-medium text-muted">Testimonial Disclosure:</strong>{' '}
                Testimonials appearing on this website may not be representative of other clients or
                customers and is not a guarantee of future performance or success.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 py-6">
          <p className="text-xs text-muted">
            {t('footer.copyright').replace('2024', currentYear)}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-[11px] text-muted/80 transition-colors hover:text-muted"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
